import { useState } from "react";

import { useNavigate } from "react-router-dom";

import { Plus, User, X } from "lucide-react";

import { Button } from "@/components/ui/button";

import { Card, CardContent } from "@/components/ui/card";

import { Input } from "@/components/ui/input";

import { Label } from "@/components/ui/label";

import { Textarea } from "@/components/ui/textarea";




import { SearchableMultiSelect } from "@/components/forms/SearchableMultiSelect";

import { useLanguage } from "@/contexts/LanguageContext";

import { useAuth } from "@/contexts/AuthContext";


import { useCreateClient, useAdminSpecializations } from "@/hooks/api/use-admin";

import { useFormValidation, commonValidationRules } from "@/hooks/useFormValidation";

import type { CreateClientRequest } from "@/api/types";

import { compressImage, isValidImageType } from "@/utils/imageCompressor";



const CreateClient = () => {

  const { t } = useLanguage();

  const { user } = useAuth();

  const navigate = useNavigate();

  const isAdmin = user?.type === 'admin';



  const [formData, setFormData] = useState<CreateClientRequest>({

    name: '',

    email: '',

    password: '',

    password_confirmation: '',

    company_name: '',

    phone: '',

    address: '',

    description: '',

    license_number: '',

    tax_id: '',

    specialization_ids: [],

  });



  const [profilePicture, setProfilePicture] = useState<File | null>(null);

  const [documents, setDocuments] = useState<File[]>([]);

  const [isCompressingImage, setIsCompressingImage] = useState(false);

  const [compressionError, setCompressionError] = useState<string | null>(null);

  // Enhanced validation
  const { errors, validateField, validateForm, clearFieldError } = useFormValidation({
    name: commonValidationRules.name,
    email: commonValidationRules.email,
    phone: commonValidationRules.phone,
    password: commonValidationRules.password,
    password_confirmation: commonValidationRules.password_confirmation,
    company_name: commonValidationRules.companyName,
    address: commonValidationRules.address,
    license_number: commonValidationRules.licenseNumber,
    tax_id: commonValidationRules.taxId,
    description: commonValidationRules.description,
  });



  const { data: specializations, isLoading: specializationsLoading } = useAdminSpecializations(

    { enabled: isAdmin && !!user }

  );



  const createMutation = useCreateClient(() => {

    // Reset form after successful creation

    setFormData({

      name: '',

      email: '',

      password: '',

      password_confirmation: '',

      company_name: '',

      phone: '',

      address: '',

      description: '',

      license_number: '',

      tax_id: '',

      specialization_ids: [],

    });

    setProfilePicture(null);

    setDocuments([]);

    // Navigate back to clients list

    navigate('/admin/dashboard/clients');

  });



  const handleInputChange = (field: keyof CreateClientRequest, value: string | number[]) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));

    // Real-time validation for string fields
    if (typeof value === 'string') {
      const formValues = {
        name: formData.name || '',
        email: formData.email || '',
        phone: formData.phone || '',
        password: formData.password || '',
        password_confirmation: formData.password_confirmation || '',
        company_name: formData.company_name || '',
        address: formData.address || '',
        license_number: formData.license_number || '',
        tax_id: formData.tax_id || '',
        description: formData.description || '',
      };
      const error = validateField(field, value, formValues);
      if (error) {
        // This will be handled by the validation hook
      } else {
        clearFieldError(field);
      }
    }
  };



  const handleProfilePictureChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!isValidImageType(file)) {
      setCompressionError(t('profile.invalidImageType') || 'نوع الملف غير صالح. يرجى رفع صورة (JPG, PNG, GIF, WebP)');
      return;
    }

    setIsCompressingImage(true);
    setCompressionError(null);

    try {
      // Compress image to max 2MB
      const compressedFile = await compressImage(file, {
        maxSizeMB: 2,
        maxWidth: 1200,
        maxHeight: 1200,
        quality: 0.85,
      });
      setProfilePicture(compressedFile);
    } catch (error) {
      console.error('Image compression failed:', error);
      setCompressionError(t('profile.compressionFailed') || 'فشل في ضغط الصورة. يرجى المحاولة مرة أخرى');
    } finally {
      setIsCompressingImage(false);
    }
  };

  const handleDocumentsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setDocuments(prev => [...prev, ...files]);
  };



  const removeDocument = (index: number) => {

    setDocuments(prev => prev.filter((_, i) => i !== index));

  };



  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validate form before submission
    const formValues = {
      name: formData.name || '',
      email: formData.email || '',
      phone: formData.phone || '',
      password: formData.password || '',
      password_confirmation: formData.password_confirmation || '',
      company_name: formData.company_name || '',
      address: formData.address || '',
      license_number: formData.license_number || '',
      tax_id: formData.tax_id || '',
      description: formData.description || '',
    };

    const isValid = validateForm(formValues);
    
    if (!isValid) {
      return; // Don't submit if validation fails
    }

    const submitData: CreateClientRequest = {
      ...formData,
      specialization_ids: formData.specialization_ids,
      ...(profilePicture && { profile_image: profilePicture }),
      ...(documents.length > 0 && { documents }),
    };

    createMutation.mutate(submitData);
  };



  const isFormValid = () => {
    return (
      formData.name?.trim() !== '' &&
      formData.email?.trim() !== '' &&
      formData.password?.trim() !== '' &&
      formData.password_confirmation?.trim() !== '' &&
      formData.password === formData.password_confirmation &&
      formData.company_name?.trim() !== '' &&
      formData.phone?.trim() !== '' &&
      Object.keys(errors).length === 0
    );
  };



  // Only admin can access

  if (!isAdmin) {

    return (

      <div className="space-y-6">

        <div className="text-center py-12">

          <p className="text-muted-foreground">

            {t('admin.clients.adminOnly') || 'هذه الصفحة متاحة فقط للمسؤولين'}

          </p>

        </div>

      </div>

    );

  }



  return (
    <div className="container mx-auto p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-foreground">
              {t('admin.clients.createClient') || 'إنشاء عميل جديد'}
            </h1>
            <p className="text-muted-foreground mt-1">
              {t('admin.clients.createDescription') || 'إنشاء حساب عميل جديد في النظام'}
            </p>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          
          {/* Left Column - Image and File Uploads */}
          <div className="lg:w-1/3">
            <div className="space-y-6">
              
              {/* Profile Picture Upload */}
              <div className="bg-white rounded-lg border p-6">
                <h3 className="text-lg font-semibold mb-4">
                  {t('profile.profilePicture') || 'الصورة الشخصية'}
                </h3>
                <div className="space-y-4">
                  <div className="aspect-square w-32 h-32 mx-auto border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50 relative">
                    {isCompressingImage ? (
                      <div className="flex flex-col items-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                        <p className="text-xs text-gray-500 mt-2">{t('profile.compressing') || 'جاري الضغط...'}</p>
                      </div>
                    ) : profilePicture ? (
                      <img
                        src={URL.createObjectURL(profilePicture)}
                        alt="Profile preview"
                        className="w-full h-full object-cover rounded-lg"
                      />
                    ) : (
                      <div className="text-center">
                        <User className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                        <p className="text-xs text-gray-500">No image</p>
                      </div>
                    )}
                  </div>
                  {compressionError && (
                    <p className="text-sm text-red-500 text-center">{compressionError}</p>
                  )}
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={handleProfilePictureChange}
                    className="cursor-pointer"
                    disabled={isCompressingImage}
                  />
                  <p className="text-xs text-gray-500">
                    {t('profile.allowedFiles') || 'JPG, PNG, GIF, WebP - سيتم ضغط الصور تلقائياً'}
                  </p>
                </div>
              </div>

              {/* Documents Upload */}
              <div className="bg-white rounded-lg border p-6">
                <h3 className="text-lg font-semibold mb-4">
                  {t('profile.documents') || 'المستندات'}
                </h3>
                <div className="space-y-4">
                  <Input
                    type="file"
                    multiple
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                    onChange={handleDocumentsChange}
                    className="cursor-pointer"
                  />
                  <p className="text-xs text-gray-500">
                    {t('profile.allowedFiles') || 'PDF, DOC, DOCX, JPG, PNG'}
                  </p>
                  
                  {/* Uploaded Documents List */}
                  {documents.length > 0 && (
                    <div className="space-y-2">
                      <h4 className="text-sm font-medium">Uploaded Files:</h4>
                      {documents.map((doc, index) => (
                        <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                          <span className="text-sm truncate flex-1">{doc.name}</span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeDocument(index)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Form Fields */}
          <div className="lg:w-2/3">
            <Card>
              <CardContent className="p-6">
                <form onSubmit={handleSubmit} className="space-y-6">
                  
                  {/* Personal Information Section */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold border-b pb-2">
                      {t('profile.personalInfo') || 'المعلومات الشخصية'}
                    </h3>
                    
                    {/* Name */}
                    <div className="space-y-2">
                      <Label htmlFor="name">
                        {t('auth.fullName') || 'الاسم الكامل'}
                      </Label>
                      <Input
                        id="name"
                        type="text"
                        value={formData.name}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                        placeholder={t('auth.fullName') || 'أدخل الاسم الكامل'}
                        required
                        className={errors.name ? 'border-red-500' : ''}
                      />
                      {errors.name && (
                        <p className="text-sm text-red-500">{errors.name}</p>
                      )}
                    </div>

                    {/* Email */}
                    <div className="space-y-2">
                      <Label htmlFor="email">
                        {t('auth.email') || 'البريد الإلكتروني'}
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        placeholder={t('auth.email') || 'أدخل البريد الإلكتروني'}
                        required
                        className={errors.email ? 'border-red-500' : ''}
                      />
                      {errors.email && (
                        <p className="text-sm text-red-500">{errors.email}</p>
                      )}
                    </div>

                    {/* Phone */}
                    <div className="space-y-2">
                      <Label htmlFor="phone">
                        {t('auth.phone') || 'رقم الهاتف'}
                      </Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => handleInputChange('phone', e.target.value)}
                        placeholder={t('auth.phone') || 'أدخل رقم الهاتف'}
                        required
                        className={errors.phone ? 'border-red-500' : ''}
                      />
                      {errors.phone && (
                        <p className="text-sm text-red-500">{errors.phone}</p>
                      )}
                    </div>
                  </div>

                  {/* Company Information Section */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold border-b pb-2">
                      {t('profile.companyInfo') || 'معلومات الشركة'}
                    </h3>
                    
                    {/* Company Name */}
                    <div className="space-y-2">
                      <Label htmlFor="company_name">
                        {t('profile.companyName') || 'اسم الشركة'}
                      </Label>
                      <Input
                        id="company_name"
                        type="text"
                        value={formData.company_name}
                        onChange={(e) => handleInputChange('company_name', e.target.value)}
                        placeholder={t('profile.companyName') || 'أدخل اسم الشركة'}
                        required
                        className={errors.company_name ? 'border-red-500' : ''}
                      />
                      {errors.company_name && (
                        <p className="text-sm text-red-500">{errors.company_name}</p>
                      )}
                    </div>

                    {/* Address */}
                    <div className="space-y-2">
                      <Label htmlFor="address">
                        {t('profile.address') || 'العنوان'}
                      </Label>
                      <Textarea
                        id="address"
                        value={formData.address}
                        onChange={(e) => handleInputChange('address', e.target.value)}
                        placeholder={t('profile.address') || 'أدخل العنوان'}
                        rows={3}
                        className={errors.address ? 'border-red-500' : ''}
                      />
                      {errors.address && (
                        <p className="text-sm text-red-500">{errors.address}</p>
                      )}
                    </div>

                    {/* License Number */}
                    <div className="space-y-2">
                      <Label htmlFor="license_number">
                        {t('profile.licenseNumber') || 'رقم الرخصة'}
                      </Label>
                      <Input
                        id="license_number"
                        type="text"
                        value={formData.license_number}
                        onChange={(e) => handleInputChange('license_number', e.target.value)}
                        placeholder={t('profile.licenseNumber') || 'أدخل رقم الرخصة'}
                        className={errors.license_number ? 'border-red-500' : ''}
                      />
                      {errors.license_number && (
                        <p className="text-sm text-red-500">{errors.license_number}</p>
                      )}
                    </div>

                    {/* Tax ID */}
                    <div className="space-y-2">
                      <Label htmlFor="tax_id">
                        {t('profile.taxId') || 'الرقم الضريبي'}
                      </Label>
                      <Input
                        id="tax_id"
                        type="text"
                        value={formData.tax_id}
                        onChange={(e) => handleInputChange('tax_id', e.target.value)}
                        placeholder={t('profile.taxId') || 'أدخل الرقم الضريبي'}
                        className={errors.taxId ? 'border-red-500' : ''}
                      />
                      {errors.taxId && (
                        <p className="text-sm text-red-500">{errors.taxId}</p>
                      )}
                    </div>

                    {/* Description */}
                    <div className="space-y-2">
                      <Label htmlFor="description">
                        {t('profile.description') || 'الوصف'}
                      </Label>
                      <Textarea
                        id="description"
                        value={formData.description}
                        onChange={(e) => handleInputChange('description', e.target.value)}
                        placeholder={t('profile.description') || 'أدخل وصف الشركة'}
                        rows={4}
                        className={errors.description ? 'border-red-500' : ''}
                      />
                      {errors.description && (
                        <p className="text-sm text-red-500">{errors.description}</p>
                      )}
                    </div>
                  </div>

                  {/* Password Section */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold border-b pb-2">
                      {t('auth.password') || 'كلمة المرور'}
                    </h3>
                    
                    {/* Password */}
                    <div className="space-y-2">
                      <Label htmlFor="password">
                        {t('auth.password') || 'كلمة المرور'}
                      </Label>
                      <Input
                        id="password"
                        type="password"
                        value={formData.password}
                        onChange={(e) => handleInputChange('password', e.target.value)}
                        placeholder={t('auth.password') || 'أدخل كلمة المرور'}
                        required
                        className={errors.password ? 'border-red-500' : ''}
                      />
                      {errors.password && (
                        <p className="text-sm text-red-500">{errors.password}</p>
                      )}
                    </div>

                    {/* Confirm Password */}
                    <div className="space-y-2">
                      <Label htmlFor="password_confirmation">
                        {t('auth.confirmPassword') || 'تأكيد كلمة المرور'}
                      </Label>
                      <Input
                        id="password_confirmation"
                        type="password"
                        value={formData.password_confirmation}
                        onChange={(e) => handleInputChange('password_confirmation', e.target.value)}
                        placeholder={t('auth.confirmPassword') || 'أكد كلمة المرور'}
                        required
                        className={errors.password_confirmation ? 'border-red-500' : ''}
                      />
                      {errors.password_confirmation && (
                        <p className="text-sm text-red-500">{errors.password_confirmation}</p>
                      )}
                    </div>
                  </div>

                  {/* Specializations */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold border-b pb-2">
                      {t('profile.specialization') || 'التخصصات'}
                    </h3>
                    {specializationsLoading ? (
                      <div className="space-y-2">
                        {[1, 2, 3].map((i) => (
                          <div key={i} className="h-10 bg-muted rounded animate-pulse" />
                        ))}
                      </div>
                    ) : (
                      <SearchableMultiSelect
                        options={specializations?.data || []}
                        selectedIds={formData.specialization_ids || []}
                        onChange={(selectedIds) => handleInputChange('specialization_ids', selectedIds)}
                        placeholder={t('profile.selectSpecializations') || 'اختر التخصصات...'}
                        searchPlaceholder={t('profile.searchSpecializations') || 'ابحث عن التخصصات...'}
                        noOptionsText={t('profile.noSpecializationsFound') || 'لم يتم العثور على تخصصات'}
                        maxVisible={3}
                      />
                    )}
                  </div>

                  {/* Submit Button */}
                  <div className="flex justify-end pt-6">
                    <Button
                      type="submit"
                      disabled={createMutation.isPending || !isFormValid()}
                      className="w-full sm:w-auto"
                    >
                      {createMutation.isPending ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                          {t('common.loading') || 'جاري التحميل...'}
                        </>
                      ) : (
                        <>
                          <Plus className="w-4 h-4 mr-2" />
                          {t('admin.clients.create') || 'إنشاء عميل'}
                        </>
                      )}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateClient;
